import { Presence } from '@radix-ui/react-presence';
import { memo, type ReactNode, useRef } from 'react';
import { useResizeDetector } from 'react-resize-detector';

import { cn } from '@air/utils-tailwind';

import { useAssetModalLeftPanel } from '~/components/AssetModalV2/AssetModalLeftPanelContextProvider';
import { AssetModalContentControlsDensityProvider } from '~/components/AssetModalV2/Content/Footer/contexts/AssetModalContentControlsDensityContext';
import { AssetModalContentDesktopLayoutFooter } from '~/components/AssetModalV2/components/AssetModalContentDesktopLayoutFooter';
import { AssetModalContentDesktopLayoutHeader } from '~/components/AssetModalV2/components/AssetModalContentDesktopLayoutHeader';
import { useFullscreenContext } from '~/components/AssetModalV2/shared/context/FullscreenContext';
import { useFullscreenAutoHide } from '~/components/AssetModalV2/Visualizer/shared/hooks/useFullscreenAutoHide';

export type AssetModalContentDesktopLayoutProps = {
  bottomLeftOverlay?: ReactNode;
  content: ReactNode;
  footer?: ReactNode;
  fullscreenOverlay?: ReactNode;
  header: ReactNode;
  leftPanel: ReactNode;
};

export const AssetModalContentDesktopLayout = memo(
  ({
    bottomLeftOverlay,
    content,
    footer,
    fullscreenOverlay,
    header,
    leftPanel,
  }: AssetModalContentDesktopLayoutProps) => {
    const fullscreenOverlayRef = useRef<HTMLDivElement | null>(null);
    const { isLeftContentPanelOpen } = useAssetModalLeftPanel();
    const { isFullscreen, fullscreenContainerRef } = useFullscreenContext();
    const { width: centerContentWidth = 0 } = useResizeDetector<HTMLDivElement>({
      handleHeight: false,
      refreshMode: 'throttle',
      refreshRate: 100,
      targetRef: fullscreenContainerRef,
    });

    // Fullscreen strips every non-viewer control, and this overlay is not dismissed by hiding it.
    const showBottomLeftOverlay = !!bottomLeftOverlay && !isFullscreen;

    useFullscreenAutoHide({
      isFullscreen,
      containerRef: fullscreenContainerRef,
      childrenRefs: [fullscreenOverlayRef],
      timeoutMs: 2000,
    });

    return (
      <div className="relative flex size-full flex-col ads:bg-base bg-gray-1">
        <AssetModalContentDesktopLayoutHeader>{header}</AssetModalContentDesktopLayoutHeader>

        <div className="flex h-full min-h-0">
          <div
            className={cn(
              'relative flex h-full min-h-0 shrink-0 flex-col py-16 transition-[width,opacity] ease-in-out',
              isLeftContentPanelOpen ? 'w-80 opacity-100' : 'w-0 opacity-0',
            )}
          >
            {isLeftContentPanelOpen && <div className="flex min-h-0 w-80 flex-1 flex-col px-4">{leftPanel}</div>}
          </div>
          <div ref={fullscreenContainerRef} className="relative flex min-h-0 grow flex-col">
            <Presence present={isFullscreen}>
              <div ref={fullscreenOverlayRef} className="opacity-0 transition-opacity duration-200 ease-in-out">
                {fullscreenOverlay}
              </div>
            </Presence>
            {content}
            {showBottomLeftOverlay && (
              <div className="pointer-events-none absolute bottom-14 left-2 z-10">{bottomLeftOverlay}</div>
            )}
            {footer ? (
              <AssetModalContentControlsDensityProvider centerContentWidth={centerContentWidth}>
                <AssetModalContentDesktopLayoutFooter>{footer}</AssetModalContentDesktopLayoutFooter>
              </AssetModalContentControlsDensityProvider>
            ) : null}
          </div>
        </div>
      </div>
    );
  },
);

AssetModalContentDesktopLayout.displayName = 'AssetModalContentDesktopLayout';
